import {
  Directive, ElementRef, Renderer2,
  AfterViewInit, AfterContentInit, OnDestroy,
  Inject, PLATFORM_ID, ContentChildren, QueryList, Input,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { StickyColumnDirective } from './sticky-column.directive';

@Directive({ selector: '[horizontalScrollableTable]', standalone: false })
export class HorizontalScrollableTableDirective implements AfterViewInit, AfterContentInit, OnDestroy {

    @Input() showScrollArrows: boolean = true;

    @ContentChildren(StickyColumnDirective, { descendants: true })
    stickyCells!: QueryList<StickyColumnDirective>;

    private rightmostCells: StickyColumnDirective[] = [];
    private leftmostCells: StickyColumnDirective[]  = [];
    private arrowsCreated = false;

    private resizeObserver?: ResizeObserver;
    private scrollListener?: () => void;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId: Object,
        private el: ElementRef,
        private renderer: Renderer2
    ) {}

    ngAfterContentInit() {
        this.stickyCells.changes.subscribe(() => this.resolveEdgeCells());
    }

    ngAfterViewInit() {
        if (!isPlatformBrowser(this.platformId)) return;
        setTimeout(() => {
            this.wrapInContainer();
            this.resolveEdgeCells();
            this.scrollListener = () => this.update();
            this.el.nativeElement.addEventListener('scroll', this.scrollListener);
            this.resizeObserver = new ResizeObserver(() => {
                this.resolveEdgeCells();
                this.update();
            });
            this.resizeObserver.observe(this.el.nativeElement);
            this.update();
        });
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();
        if (this.scrollListener) {
            this.el.nativeElement.removeEventListener('scroll', this.scrollListener);
        }
        const wrapper   = this.el.nativeElement as HTMLElement;
        const container = wrapper.parentElement;
        if (container?.hasAttribute('data-scroll-container')) {
            container.parentElement?.insertBefore(wrapper, container);
            container.remove();
        }
    }

    private wrapInContainer() {
        const wrapper = this.el.nativeElement as HTMLElement;
        const parent  = wrapper.parentElement;
        if (!parent || parent.hasAttribute('data-scroll-container')) return;

        const container = this.renderer.createElement('div') as HTMLElement;
        container.setAttribute('data-scroll-container', '');
        Object.assign(container.style, {
            display: 'flex',
            alignItems: 'stretch',
        });

        parent.insertBefore(container, wrapper);
        container.appendChild(wrapper);
    }

    private resolveEdgeCells() {
        const wrapper  = this.el.nativeElement as HTMLElement;
        const theadRow = wrapper.querySelector('thead tr') as HTMLElement;
        if (!theadRow) return;

        const allCells   = this.stickyCells?.toArray() ?? [];
        const theadCells = allCells.filter(c =>
          theadRow.contains(c.el.nativeElement) && !c.stickyDisabled
        );

        const rightmostHead = theadCells.find(c => !c.stickyRight && c.isRightmost);
        const leftmostHead  = theadCells.find(c =>  c.stickyRight && c.isLeftmost);

        const rightmostIndex = rightmostHead
            ? Array.from(theadRow.children).indexOf(rightmostHead.el.nativeElement)
            : -1;
        const leftmostIndex = leftmostHead
            ? Array.from(theadRow.children).indexOf(leftmostHead.el.nativeElement)
            : -1;

        this.rightmostCells = rightmostIndex >= 0
            ? allCells.filter(c => {
                const row = c.el.nativeElement.parentElement;
                return row && Array.from(row.children).indexOf(c.el.nativeElement) === rightmostIndex;
            })
            : [];

        this.leftmostCells = leftmostIndex >= 0
            ? allCells.filter(c => {
                const row = c.el.nativeElement.parentElement;
                return row && Array.from(row.children).indexOf(c.el.nativeElement) === leftmostIndex;
            })
            : [];
    }

    private createArrows() {
        if (this.arrowsCreated) return;
        this.arrowsCreated = true;

        const wrapper   = this.el.nativeElement as HTMLElement;
        const container = wrapper.parentElement;
        if (!container) return;

        const box = this.renderer.createElement('div') as HTMLElement;
        box.setAttribute('data-scroll-arrow-box', '');
        Object.assign(box.style, {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'start',
            width: '56px',
            flexShrink: '0',
        });
        container.appendChild(box);

        (['right', 'left'] as const).forEach(side => {
            const btn = this.renderer.createElement('div') as HTMLElement;
            Object.assign(btn.style, {
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '40px',
                width: '100%',
            });
            const img = this.renderer.createElement('img') as HTMLImageElement;
            img.src = `assets/icons/icons-alt/arrow-${side}.svg`;
            img.alt = `Scroll ${side}`;
            btn.appendChild(img);
            btn.setAttribute('data-scroll-arrow', side);
            btn.addEventListener('click', () => {
                // page, then snap so a scrollable column lands flush against the
                // sticky-left columns (fully visible) instead of cut off
                this.scrollByPage(wrapper, side === 'right' ? 1 : -1);
            });
            box.appendChild(btn);
        });
    }

    /** Width of the sticky columns currently pinned on each side of the viewport. */
    private pinnedWidths(theadRow: HTMLElement | null): { left: number; right: number } {
        let left = 0, right = 0;
        if (theadRow) {
            (this.stickyCells?.toArray() ?? []).forEach(c => {
                const el = c.el.nativeElement as HTMLElement;
                if (theadRow.contains(el) && c.pinned) {
                    if (c.stickyRight) right += el.offsetWidth;
                    else left += el.offsetWidth;
                }
            });
        }
        return { left, right };
    }

    /** Page by the visible band, then snap the band's left edge to a scrollable
     *  column boundary so that column is fully visible right after the sticky cols. */
    private scrollByPage(wrapper: HTMLElement, dir: 1 | -1) {
        const theadRow = wrapper.querySelector('thead tr') as HTMLElement | null;
        const { left: stickyLeft, right: stickyRight } = this.pinnedWidths(theadRow);

        const wrapLeft = wrapper.getBoundingClientRect().left;
        const scrollLeft = wrapper.scrollLeft;

        // content-x of each non-horizontally-pinned header column's left edge
        // (released sticky cells are position:sticky for top-stick, but not pinned)
        const boundaries = theadRow
            ? (Array.from(theadRow.children) as HTMLElement[])
                .filter(el => !(el as any).__stickyDirective?.pinned)
                .map(el => el.getBoundingClientRect().left - wrapLeft + scrollLeft)
                .sort((a, b) => a - b)
            : [];

        if (!boundaries.length) {
            wrapper.scrollBy({ left: dir * wrapper.clientWidth, behavior: 'smooth' });
            return;
        }

        const band = Math.max(120, wrapper.clientWidth - stickyLeft - stickyRight);
        const eps = 2;
        const bandLeft = scrollLeft + stickyLeft;

        // pick the column boundary to place at the band's left edge:
        // furthest reachable within one band in the press direction, at least one column.
        let targetLeft: number;
        if (dir > 0) {
            const reach = boundaries.filter(b => b > bandLeft + eps && b <= bandLeft + band + eps);
            targetLeft = reach.length ? reach[reach.length - 1]
                : (boundaries.find(b => b > bandLeft + eps) ?? bandLeft);
        } else {
            const reach = boundaries.filter(b => b < bandLeft - eps && b >= bandLeft - band - eps);
            targetLeft = reach.length ? reach[0]
                : ([...boundaries].reverse().find(b => b < bandLeft - eps) ?? boundaries[0]);
        }

        const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
        const newScrollLeft = Math.max(0, Math.min(maxScroll, targetLeft - stickyLeft));
        wrapper.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }

    private removeArrows() {
        if (!this.arrowsCreated) return;
        this.arrowsCreated = false;
        const wrapper   = this.el.nativeElement as HTMLElement;
        const container = wrapper.parentElement;
        container?.querySelector('[data-scroll-arrow-box]')?.remove();
    }

    private update() {
        const wrapper   = this.el.nativeElement as HTMLElement;
        const container = wrapper.parentElement;
        if (!container) return;

        const scrollLeft = wrapper.scrollLeft;
        const maxScroll  = wrapper.scrollWidth - wrapper.clientWidth;
        const canScroll  = maxScroll > 0;

        if (canScroll && this.showScrollArrows) {
            this.createArrows();
        } else {
            this.removeArrows();
        }

        const leftArrow  = container.querySelector('[data-scroll-arrow="left"]')  as HTMLElement;
        const rightArrow = container.querySelector('[data-scroll-arrow="right"]') as HTMLElement;
        if (leftArrow && rightArrow) {
            this.renderer.setStyle(leftArrow,  'opacity', scrollLeft > 0 ? '1' : '0.3');
            this.renderer.setStyle(rightArrow, 'opacity', scrollLeft < maxScroll ? '1' : '0.3');
        }

        this.stickyCells?.forEach(c => {
            this.renderer.removeClass(c.el.nativeElement, 'right-shadow');
            this.renderer.removeClass(c.el.nativeElement, 'left-shadow');
        });

      if (canScroll) {
        this.rightmostCells.forEach(c => this.renderer.addClass(c.el.nativeElement, 'right-shadow'));
      }
      if (canScroll && scrollLeft < maxScroll) {
        this.leftmostCells.forEach(c => this.renderer.addClass(c.el.nativeElement, 'left-shadow'));
      }
    }
}
